(() => {
  if (customElements.get('rg-creator-social')) return;
  class RocketCreatorSocial extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-rocket-people]');
      if (!this.section || this.cleanup) return;
      this.cleanup = new AbortController();
      // Keep social controls independent from the stage's swipe/arrow navigation.
      ['pointerdown', 'pointerup', 'keydown'].forEach(type => {
        this.addEventListener(type, event => event.stopPropagation(), { signal: this.cleanup.signal });
      });
      this.panels = [...this.querySelectorAll('[data-creator-id]')];
      this.visible = !('IntersectionObserver' in window);
      this.status = null;
      this.inFlight = false;
      this.observer = new MutationObserver(() => this.sync());
      const list = this.section.querySelector('.rocket-people__list');
      if (list) this.observer.observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
      this.addEventListener('toggle', event => {
        if (event.target.matches('[data-creator-video]')) this.syncVideo(event.target);
      }, { capture: true, signal: this.cleanup.signal });
      document.addEventListener('visibilitychange', () => {
        this.paintStatus();
        if (document.visibilityState === 'visible') this.refreshStatus();
      }, { signal: this.cleanup.signal });
      this.sync();
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          if (this.visible) this.refreshStatus();
        }, { rootMargin: '200px' });
        this.intersection.observe(this.section);
      }
      if (this.dataset.statusEndpoint) {
        this.poll = setInterval(() => { this.paintStatus(); this.refreshStatus(); }, 60000);
        this.refreshStatus();
      }
    }

    disconnectedCallback() {
      this.cleanup?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.request?.abort();
      clearInterval(this.poll);
      clearTimeout(this.requestTimeout);
      this.querySelectorAll('iframe').forEach(frame => frame.remove());
      this.cleanup = null;
    }

    sync() {
      const selected = this.section.querySelector('.rocket-people__person[aria-pressed="true"]');
      const id = selected?.dataset.peopleBlockId;
      let hasPanel = false;
      this.panels.forEach(panel => {
        panel.hidden = panel.dataset.creatorId !== id;
        if (!panel.hidden) hasPanel = true;
        const video = panel.querySelector('[data-creator-video]');
        if (video) this.syncVideo(video);
      });
      this.hidden = !hasPanel;
    }

    syncVideo(video) {
      const panel = video.closest('[data-creator-id]');
      const holder = video.querySelector('[data-youtube-playlist]');
      if (!holder) return;
      if (panel.hidden || !video.open) {
        holder.querySelector('iframe')?.remove();
        return;
      }
      const playlist = holder.dataset.youtubePlaylist;
      if (!/^(UU|PL)[A-Za-z0-9_-]{8,78}$/.test(playlist || '') || holder.querySelector('iframe')) return;
      const url = new URL('https://www.youtube-nocookie.com/embed/videoseries');
      url.searchParams.set('list', playlist);
      url.searchParams.set('index', '0');
      url.searchParams.set('autoplay', '0');
      url.searchParams.set('rel', '0');
      const frame = document.createElement('iframe');
      frame.src = url.href;
      frame.title = holder.dataset.videoTitle || 'YouTube';
      frame.loading = 'lazy';
      frame.allow = 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      holder.append(frame);
    }

    async refreshStatus() {
      if (!this.dataset.statusEndpoint || !this.visible || this.inFlight || document.visibilityState !== 'visible') return;
      let endpoint;
      try {
        endpoint = new URL(this.dataset.statusEndpoint, location.origin);
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) return;
      } catch { return; }
      this.inFlight = true;
      this.request = new AbortController();
      this.requestTimeout = setTimeout(() => this.request.abort(), 8000);
      try {
        const response = await fetch(endpoint.href, {
          signal: this.request.signal, credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Status unavailable');
        const payload = await response.json();
        if (!Array.isArray(payload.streams) || !Number.isFinite(Date.parse(payload.updated_at))) throw new Error('Invalid status');
        this.status = payload;
      } catch {
        this.status = null;
      } finally {
        clearTimeout(this.requestTimeout);
        this.inFlight = false;
        if (this.isConnected) this.paintStatus();
      }
    }

    paintStatus() {
      const age = this.status ? Date.now() - Date.parse(this.status.updated_at) : Infinity;
      const fresh = age >= -30000 && age < 180000;
      const liveUsers = new Set(fresh ? this.status.streams
        .filter(stream => stream && stream.type === 'live' && typeof stream.user_login === 'string')
        .map(stream => stream.user_login.toLowerCase()) : []);
      this.panels.forEach(panel => {
        const username = panel.dataset.twitchUser || '';
        const known = fresh && /^[a-z0-9_]{1,25}$/.test(username);
        const live = known && liveUsers.has(username);
        const link = panel.querySelector('[data-creator-live-link]');
        if (link) link.hidden = !live;
        const offline = panel.querySelector('[data-creator-offline]');
        if (offline) offline.hidden = !known || live;
        this.section.querySelectorAll('[data-creator-live-flag]').forEach(flag => {
          if (flag.dataset.creatorLiveFlag !== panel.dataset.creatorId) return;
          flag.hidden = !known;
          flag.dataset.state = live ? 'live' : 'offline';
          flag.textContent = live ? flag.dataset.liveLabel : flag.dataset.offlineLabel;
        });
      });
    }
  }
  customElements.define('rg-creator-social', RocketCreatorSocial);
})();
